import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import React, { useEffect } from 'react'
import Grid from '@mui/material/Grid'

import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormControl from '@mui/material/FormControl'
import Checkbox from '@mui/material/Checkbox'
import Link from '@mui/material/Link'
import { Step1Props } from '../types'
import { Controller } from 'react-hook-form'
import useStyles from '../styles/Step1.styles'
import FormItem from '../../../hoc/FormItem'
import { useSnackbar } from 'notistack'
import { Alert, Button, CircularProgress, Divider, FormLabel, Typography } from '@mui/material'
import HelperModal from '../../../hoc/HelperModal'
import { useGtmPageView } from '../../hooks'
import TagManager from 'react-gtm-module'
import useActivationObject from '../../../providers/ActivationObjectProvider/hooks/useActivationObject'
import { Portal } from 'react-portal'
import { useHelpText } from '../../../../api/texts/hooks'

const Step1View: React.FC<Step1Props> = ({ form, conditions, options, isUpdating, file, activationObject }) => {
    const classes = useStyles()
    const { control, formState, handleSubmit, watch, setValue } = form.form
    const { finish, finishPcoObject } = useActivationObject()
    const { errors } = formState
    const personTypeState = watch('personType')
    const isPhysical = personTypeState === 'Physical'
    const { data: redirectText } = useHelpText('REDIRECT_ZZ')

    const { enqueueSnackbar } = useSnackbar()

    useEffect(() => {
        if (options.contracts.error)
            enqueueSnackbar('Chyba při načítání kontraktů: ' + options.contracts.error, { variant: 'error' })
    }, [enqueueSnackbar, options.contracts.error])

    // TODO: revisit if there's a better way to stop re-render
    useEffect(() => {
        if (finishPcoObject) {
            setTimeout(() => {
                finish()
                if (process.env.REACT_APP_ZZ) window.location.href = process.env.REACT_APP_ZZ
            }, 2000)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useGtmPageView({ pageName: 'Step1', synonym: activationObject?.synonym })

    return (
        <>
            {finishPcoObject && redirectText && (
                <Portal>
                    <div className={classes.overlay}>
                        <div className={classes.overlayContent}>
                            <span dangerouslySetInnerHTML={{ __html: redirectText }}></span>
                            <CircularProgress />
                        </div>
                    </div>
                </Portal>
            )}
            <form onSubmit={handleSubmit(form.onSubmit)} method='post'>
                <Box p={2} maxWidth={'1050px'}>
                    <Box p={1} paddingBottom={{ sm: 0 }} textAlign={'left'}>
                        <Typography fontSize={{ xs: '1.2rem', sm: '1.973rem' }} variant='h4'>
                            {' '}
                            ZÁKLADNÍ INFORMACE
                        </Typography>
                    </Box>
                    <Box p={1} paddingTop={0} fontSize={'0.875rem'} textAlign={'left'}>
                        <Typography variant='body1' paragraph>
                            Pro pokračování potřebujeme nejprve ověřit, že jste oprávněn/a uzavřít smlouvu a potvrdit komunikační
                            a registrační údaje.
                        </Typography>
                    </Box>
                    <Alert severity='info' sx={{ marginBottom: 1 }}>
                        Zákazník uzavřením Smlouvy ubezpečuje Poskytovatele, že je oprávněn Smlouvu uzavřít, užívat Objekt a že z
                        jeho strany nedochází ke sjednání Služby za účelem podpory či ochrany jednání, které je v rozporu s obecně
                        závaznými právními předpisy či právem třetí osoby k Objektu, a ani takto nebude využívána. Zákazník má
                        právo v souladu s občanským zákoníkem na odstoupení od smlouvy do 14 dnů od jejího uzavření, vzorový
                        formulář{' '}
                        <a
                            className={classes.infoLink}
                            href='https://www.bezpecnostnicentrum.cz/file/edee/2018/12/vzorovy-formular-pro-odstoupeni-od-smlouvy.pdf'
                            target='blank'
                        >
                            ZDE
                        </a>
                    </Alert>
                    <Divider />
                    <Grid container spacing={{ xs: 1, md: 2 }}>
                        <Grid item xs={12} md={2}>
                            <FormItem>
                                <FormLabel className={classes.formLabelBlock}>
                                    Zákazník{' '}
                                    <Typography fontSize='small' component='span'>
                                        (jméno a příjmení)
                                    </Typography>
                                </FormLabel>
                            </FormItem>
                        </Grid>
                        <Grid item xs={12} md={10}>
                            <FormItem>
                                <FormControl fullWidth>
                                    <Controller
                                        name={'personName'}
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                id='competentPerson'
                                                fullWidth
                                                label=''
                                                variant='outlined'
                                                InputProps={{
                                                    endAdornment: <HelperModal textKey='AUTHORIZED_PERSON' />,
                                                }}
                                                {...field}
                                                error={errors.personName !== undefined}
                                                helperText={errors.personName?.message}
                                            />
                                        )}
                                    />
                                </FormControl>
                            </FormItem>
                        </Grid>
                        <Grid item container rowSpacing={1} sm={6}>
                            <Grid item xs={12} md={4}>
                                <FormItem>
                                    <FormLabel className={classes.formLabelBlock}>Telefonní číslo</FormLabel>
                                </FormItem>
                            </Grid>
                            <Grid paddingLeft={{ md: 0.65 }} item xs={12} md={8}>
                                <FormItem>
                                    <FormControl fullWidth>
                                        <Controller
                                            name={'personPhoneNumber'}
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    id='phone'
                                                    {...field}
                                                    fullWidth
                                                    variant='outlined'
                                                    error={errors.personPhoneNumber !== undefined}
                                                    helperText={errors.personPhoneNumber?.message}
                                                />
                                            )}
                                        />
                                    </FormControl>
                                </FormItem>
                            </Grid>
                        </Grid>
                        <Grid item container rowSpacing={1} sm={6}>
                            <Grid item xs={12} md={4}>
                                <FormItem>
                                    <FormLabel className={classes.formLabelBlock}>E-mailová adresa</FormLabel>
                                </FormItem>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <FormItem>
                                    <FormControl fullWidth>
                                        <Controller
                                            name={'personEmail'}
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    id='email'
                                                    {...field}
                                                    fullWidth
                                                    variant='outlined'
                                                    error={errors.personEmail !== undefined}
                                                    helperText={errors.personEmail?.message}
                                                />
                                            )}
                                        />
                                    </FormControl>
                                </FormItem>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} sm={8} md={2}>
                            <FormItem>
                                <FormLabel className={classes.formLabelBlock}>Právní forma</FormLabel>
                            </FormItem>
                        </Grid>

                        <Grid item xs={12} sm={8} md={4}>
                            <FormItem>
                                <FormControl component='fieldset'>
                                    <Controller
                                        name={'personType'}
                                        control={control}
                                        render={({ field }) => (
                                            <RadioGroup row aria-label='personType' {...field}>
                                                <FormControlLabel
                                                    value='Physical'
                                                    control={
                                                        <Radio
                                                            onClick={() => setValue('personAuthorisationType', undefined)}
                                                            color='primary'
                                                        />
                                                    }
                                                    label='Fyzická osoba'
                                                />
                                                <FormControlLabel
                                                    value='Legal'
                                                    control={
                                                        <Radio
                                                            onClick={() => setValue('personAuthorisationType', undefined)}
                                                            color='primary'
                                                        />
                                                    }
                                                    label='Právnická osoba'
                                                />
                                            </RadioGroup>
                                        )}
                                    />
                                </FormControl>
                            </FormItem>
                        </Grid>
                        <Grid item xs={12} sm={8} md={2}>
                            <FormItem>
                                <FormLabel className={classes.formLabelBlock}>Druh oprávnění</FormLabel>
                            </FormItem>
                        </Grid>
                        <Grid item xs={12} sm={8} md={4} paddingLeft={{ md: '11px !important' }}>
                            <FormItem>
                                <Controller
                                    name={'personAuthorisationType'}
                                    control={control}
                                    defaultValue={isPhysical ? 'Owener' : 'StatutoryAuthority'}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            select
                                            fullWidth
                                            variant='outlined'
                                            error={errors.personAuthorisationType !== undefined}
                                            helperText={errors.personAuthorisationType?.message}
                                        >
                                            {options.permissions
                                                .filter((option) =>
                                                    isPhysical ? option.isPhysical === true : option.isPhysical === false
                                                )
                                                .map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                        </TextField>
                                    )}
                                />
                            </FormItem>
                        </Grid>

                        {conditions.showAnotherAuthorisationType && (
                            <>
                                <Grid item xs={12} sm={8} md={6}></Grid>
                                <Grid item xs={12} sm={8} md={2}>
                                    <FormItem>
                                        <FormLabel className={classes.formLabelBlock}>Jiný druh oprávnění</FormLabel>
                                    </FormItem>
                                </Grid>
                                <Grid item xs={12} sm={8} md={4}>
                                    <FormItem>
                                        <Controller
                                            name={'anotherAuthorisationType'}
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    variant='outlined'
                                                    error={errors.anotherAuthorisationType !== undefined}
                                                    helperText={errors.anotherAuthorisationType?.message}
                                                ></TextField>
                                            )}
                                        />
                                    </FormItem>
                                </Grid>
                            </>
                        )}

                        {conditions.showUploadFile && (
                            <>
                                <Grid item xs={12} sm={8} md={3}>
                                    <FormItem>
                                        <FormLabel>Nahrát dokument o plné moci</FormLabel>
                                    </FormItem>
                                </Grid>
                                <Grid fontSize='x-large' item xs={12} sm={8} md={9}>
                                    <FormItem marginBottom={file.showFileError ? 2 : 0}>
                                        <Controller
                                            name={'uploadedFile'}
                                            control={control}
                                            render={({ field }) => (
                                                <FormItem>
                                                    <input
                                                        type='file'
                                                        ref={file.fileRef}
                                                        onChange={file.handleFileChange}
                                                        accept='.pdf, .jpg, .jpeg'
                                                    />
                                                </FormItem>
                                            )}
                                        />
                                    </FormItem>
                                    {file.showFileError && (
                                        <Alert severity='warning' variant='outlined'>
                                            {`Chyba při nahrávani souboru: ${file.resultMessage}`}
                                        </Alert>
                                    )}
                                </Grid>
                            </>
                        )}
                        {/* TODO: remove style opacity and disabled property when needed */}
                        {/* <Grid className={classes.longLabelText} item xs={12} md={5}>
                        <FormItem marginTop={{ xs: '10px', md: 0 }}>
                            <FormLabel>
                                Chcete použít nastavení z jiné smlouvy?&nbsp;
                                <HelperModal textKey='COPY_INVOICE_NUMBER' />
                            </FormLabel>
                        </FormItem>
                    </Grid> */}
                        {/* <Grid item xs={12} md={3}> */}
                        <Grid item xs={12} style={{ opacity: 0 }}>
                            <FormItem>
                                <FormControl fullWidth>
                                    <Controller
                                        name={'useSettingsAnotherContract'}
                                        control={control}
                                        render={({ field }) => (
                                            <RadioGroup
                                                row
                                                aria-label='gender'
                                                {...field}
                                                onChange={(e, val) => field.onChange(val === 'true')}
                                            >
                                                <FormControlLabel
                                                    disabled
                                                    value={true}
                                                    control={<Radio color='primary' />}
                                                    label='Ano'
                                                />
                                                <FormControlLabel
                                                    value={false}
                                                    disabled
                                                    control={<Radio color='primary' value={false} />}
                                                    label='Ne'
                                                />
                                            </RadioGroup>
                                        )}
                                    />
                                </FormControl>
                            </FormItem>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            {conditions.showContracts && (
                                <FormItem>
                                    <Controller
                                        name={'numberAnotherContract'}
                                        control={form.form.control}
                                        render={({ field }) => (
                                            <TextField
                                                select
                                                fullWidth
                                                {...field}
                                                onChange={(e) => {
                                                    form.form.setValue('numberAnotherContract', e.target.value)
                                                }}
                                                error={errors.numberAnotherContract !== undefined}
                                                helperText={errors.numberAnotherContract?.message}
                                            >
                                                {options.contracts.data &&
                                                    options.contracts.data.map((option, index) => (
                                                        <MenuItem key={index} value={option.numberContract}>
                                                            {option.numberContract}
                                                        </MenuItem>
                                                    ))}
                                            </TextField>
                                        )}
                                    />
                                </FormItem>
                            )}
                        </Grid>
                        {conditions.showContracts && (
                            <>
                                <Grid item xs={12} md={5}>
                                    <FormItem>
                                        <FormLabel>
                                            Chcete sjednotit fakturaci s uvedeným objektem?&nbsp;
                                            <HelperModal textKey='COPY_INVOICE_NUMBER_UNITE_INVOICE_' />
                                        </FormLabel>
                                    </FormItem>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <FormItem>
                                        <FormControl fullWidth>
                                            <Controller
                                                name={'unifyInvoicing'}
                                                control={control}
                                                render={({ field }) => (
                                                    <RadioGroup
                                                        row
                                                        {...field}
                                                        onChange={(e, val) => field.onChange(val === 'true')}
                                                    >
                                                        <FormControlLabel
                                                            value={true}
                                                            control={<Radio color='primary' />}
                                                            label='ANO'
                                                        />
                                                        <FormControlLabel
                                                            value={false}
                                                            control={<Radio color='primary' value={false} />}
                                                            label='NE'
                                                        />
                                                    </RadioGroup>
                                                )}
                                            />
                                        </FormControl>
                                    </FormItem>
                                </Grid>
                            </>
                        )}
                        <Grid container item xs={12}>
                            <Grid item xs={1.2} sm={0.75} md={0.5}>
                                <Controller
                                    name={'check1'}
                                    control={form.form.control}
                                    render={({ field }) => <Checkbox {...field} checked={field.value} color='primary' />}
                                />
                            </Grid>
                            <Grid item xs={10.8} sm={11} md={11.5}>
                                <FormLabel>
                                    <Typography paddingTop={{ xs: 1, sm: 1.2, md: 1.3, lg: 1.8 }}>
                                        Seznámil/a jsem se se Zásadami zpracování&nbsp;
                                        <Link
                                            target='_blank'
                                            className={classes.link}
                                            href='https://www.bezpecnostnicentrum.cz/file/edee/2025/02/f-18-09-zasady-zpracovani-osobnich-udaju.pdf'
                                        >
                                            osobních údajů
                                        </Link>
                                        &nbsp;a jejich obsahu rozumím
                                    </Typography>
                                </FormLabel>
                                {errors.check1 && (
                                    <Box fontSize={'0.875rem'} color={'red'}>
                                        {errors.check1?.message}
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                        <Grid container item xs={12}>
                            <Grid item xs={1.2} sm={0.75} md={0.5}>
                                <Controller
                                    name={'check2'}
                                    control={form.form.control}
                                    render={({ field }) => <Checkbox {...field} checked={field.value} color='primary' />}
                                />
                            </Grid>
                            <Grid item xs={10.8} sm={11} md={11.5}>
                                <FormLabel>
                                    <Typography paddingTop={{ xs: 1, sm: 1.2, md: 1.3, lg: 1.8 }}>
                                        Seznámil/a jsem se s&nbsp;
                                        <Link
                                            target='_blank'
                                            className={classes.link}
                                            href='https://www.bezpecnostnicentrum.cz/file/edee/2020/11/f-17-10-03_vseobecne-obchodni-podminky_ucinne-od-31_10_2020.pdf'
                                        >
                                            Všeobecnými obchodními podmínkami
                                        </Link>
                                        &nbsp;a jejich obsahu rozumím
                                    </Typography>
                                </FormLabel>
                                {errors.check2 && (
                                    <Box fontSize={'0.875rem'} color={'red'}>
                                        {errors.check2?.message}
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>

                    <Box p={1} textAlign={'right'}>
                        <Button
                            variant='contained'
                            color='primary'
                            type='submit'
                            disabled={isUpdating}
                            startIcon={isUpdating && <CircularProgress size={20} />}
                            onClick={() => {
                                TagManager.dataLayer({
                                    dataLayer: {
                                        event: 'clickNext',
                                    },
                                })
                            }}
                        >
                            Pokračovat &#62;
                        </Button>
                    </Box>
                </Box>
            </form>
        </>
    )
}

export default Step1View
